import { Link } from "react-router-dom";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { supabase, rowToCharacter, CharacterRow } from "@/lib/supabase";

// Hero images — portrait orientation optimized for mobile (group of multiple women)
const HERO_IMAGES_MOBILE = [
  "https://public.readdy.ai/ai/img_res/a5af0776d0ccb29da8e67d0c04c7479f.jpg",
  "https://readdy.ai/api/search-image?query=four%20cute%20young%20japanese%20women%20posing%20together%20cheerfully%2C%20diverse%20hairstyles%20and%20colorful%20casual%20outfits%2C%20shinjuku%20tokyo%20night%20street%20background%20with%20glowing%20neon%20signs%20softly%20blurred%2C%20all%20smiling%20brightly%2C%20warm%20amber%20and%20pink%20tones%2C%20portrait%20lifestyle%20photography%2C%20group%20shot%20upper%20bodies%2C%20fun%20and%20lively%20atmosphere%2C%20bokeh%20city%20lights&width=390&height=844&seq=hero_grp_mob_v2&orientation=portrait",
  "https://readdy.ai/api/search-image?query=three%20stylish%20young%20japanese%20women%20friends%20standing%20together%20laughing%2C%20harajuku%20omotesando%20street%20background%20softly%20blurred%2C%20trendy%20colorful%20fashion%2C%20long%20and%20short%20hair%20mix%2C%20bright%20daytime%20warm%20light%2C%20cheerful%20group%20portrait%2C%20soft%20pink%20and%20warm%20tones%2C%20lifestyle%20photography%2C%20upper%20bodies%20centered%2C%20joyful%20expressions&width=390&height=844&seq=hero_grp_mob_v3&orientation=portrait",
  "https://readdy.ai/api/search-image?query=two%20beautiful%20young%20japanese%20women%20sitting%20together%20at%20outdoor%20cafe%20terrace%2C%20leaning%20in%20close%20and%20laughing%2C%20wearing%20cute%20summer%20dresses%2C%20warm%20evening%20golden%20light%2C%20tokyo%20cityscape%20softly%20blurred%20behind%20them%2C%20pink%20and%20peach%20tones%2C%20intimate%20and%20fun%20atmosphere%2C%20portrait%20lifestyle%20photography%2C%20both%20faces%20clearly%20visible%20and%20happy&width=390&height=844&seq=hero_grp_mob_v4&orientation=portrait",
  "https://readdy.ai/api/search-image?query=group%20of%20three%20young%20japanese%20women%20standing%20together%20in%20front%20of%20illuminated%20neon%20sign%20wall%20at%20night%2C%20all%20wearing%20trendy%20streetwear%20fashion%2C%20vibrant%20pink%20and%20magenta%20neon%20light%20reflections%20on%20faces%2C%20smiling%20and%20playful%20poses%2C%20urban%20night%20photography%2C%20warm%20glow%2C%20full%20upper%20bodies%2C%20bokeh%20street%20background&width=390&height=844&seq=hero_grp_mob_v5&orientation=portrait",
  "https://readdy.ai/api/search-image?query=five%20cute%20young%20japanese%20women%20gathered%20together%20on%20rooftop%20with%20city%20skyline%20at%20twilight%2C%20all%20in%20elegant%20casual%20outfits%2C%20soft%20pink%20and%20orange%20sunset%20sky%20behind%20them%2C%20happy%20relaxed%20group%20expressions%2C%20lifestyle%20photography%2C%20warm%20golden%20tones%2C%20romantic%20city%20evening%20atmosphere%2C%20upper%20bodies%20visible&width=390&height=844&seq=hero_grp_mob_v6&orientation=portrait",
];

// Hero images — landscape for desktop (group of multiple women)
const HERO_IMAGES_DESKTOP = [
  "https://readdy.ai/api/search-image?query=group%20of%20four%20beautiful%20young%20japanese%20women%20friends%20laughing%20together%20on%20tokyo%20shibuya%20city%20street%2C%20diverse%20stylish%20casual%20fashion%2C%20long%20silky%20hair%2C%20bright%20warm%20smiles%2C%20neon%20signs%20and%20buildings%20softly%20blurred%20behind%2C%20warm%20evening%20pink%20light%2C%20vibrant%20urban%20atmosphere%2C%20group%20lifestyle%20photography%2C%20all%20faces%20visible%20and%20happy%2C%20rose%20gold%20tones&width=1200&height=750&seq=hero_grp_dsk_v1&orientation=landscape",
  "https://readdy.ai/api/search-image?query=three%20gorgeous%20young%20japanese%20women%20posing%20together%20happily%20in%20front%20of%20shinjuku%20tokyo%20night%20street%20background%20with%20glowing%20signs%20blurred%2C%20elegant%20casual%20outfits%2C%20natural%20makeup%2C%20golden%20hour%20warm%20light%2C%20all%20faces%20fully%20visible%20and%20smiling%2C%20lifestyle%20group%20photography%2C%20warm%20amber%20tones%2C%20urban%20chic%20atmosphere&width=1200&height=750&seq=hero_grp_dsk_v2&orientation=landscape",
  "https://readdy.ai/api/search-image?query=group%20of%20five%20cute%20young%20japanese%20women%20friends%20standing%20together%20smiling%20brightly%2C%20harajuku%20omotesando%20street%20background%20with%20trees%20and%20boutiques%20softly%20blurred%2C%20trendy%20fashion%20mix%2C%20long%20wavy%20hair%2C%20bright%20daytime%20light%2C%20cheerful%20group%20expression%2C%20lifestyle%20photography%2C%20soft%20pink%20and%20green%20tones%2C%20all%20subjects%20clearly%20visible&width=1200&height=750&seq=hero_grp_dsk_v3&orientation=landscape",
  "https://readdy.ai/api/search-image?query=two%20young%20japanese%20women%20sitting%20side%20by%20side%20at%20elegant%20rooftop%20bar%2C%20tokyo%20skyline%20glittering%20behind%20them%20at%20night%2C%20both%20wearing%20chic%20evening%20outfits%2C%20warm%20candlelight%20and%20neon%20reflections%2C%20intimate%20and%20lively%20conversation%2C%20lifestyle%20night%20photography%2C%20warm%20pink%20and%20gold%20tones%2C%20both%20faces%20fully%20visible%20and%20smiling&width=1200&height=750&seq=hero_grp_dsk_v4&orientation=landscape",
  "https://readdy.ai/api/search-image?query=group%20of%20three%20beautiful%20young%20japanese%20women%20walking%20together%20on%20tokyo%20bridge%20at%20sunset%2C%20golden%20hour%20warm%20light%20casting%20long%20shadows%2C%20all%20wearing%20stylish%20casual%20fashion%2C%20laughing%20freely%2C%20city%20skyline%20and%20pink%20sky%20behind%20them%2C%20travel%20lifestyle%20photography%2C%20warm%20orange%20and%20pink%20tones%2C%20vibrant%20and%20joyful%20mood&width=1200&height=750&seq=hero_grp_dsk_v5&orientation=landscape",
  "https://readdy.ai/api/search-image?query=four%20young%20japanese%20women%20gathered%20on%20a%20bright%20rooftop%20terrace%20with%20tokyo%20city%20view%2C%20all%20in%20pastel%20and%20white%20summer%20outfits%2C%20smiling%20and%20toasting%20with%20drinks%2C%20soft%20afternoon%20sunlight%2C%20cheerful%20and%20elegant%20atmosphere%2C%20lifestyle%20photography%2C%20warm%20cream%20and%20blush%20pink%20tones%2C%20all%20faces%20clearly%20visible&width=1200&height=750&seq=hero_grp_dsk_v6&orientation=landscape",
];

const STATS = [
  { icon: "ri-user-heart-fill", value: "120+", label: "登録中の女の子", color: "from-pink-400 to-rose-400" },
  { icon: "ri-map-pin-2-fill", value: "全国", label: "対応エリア", color: "from-rose-400 to-pink-500" },
  { icon: "ri-time-fill", value: "15分毎", label: "位置情報更新", color: "from-pink-500 to-fuchsia-400" },
  { icon: "ri-heart-3-fill", value: "毎日", label: "新規追加", color: "from-fuchsia-400 to-pink-400" },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    icon: "ri-crosshair-2-fill",
    title: "現在地を許可する",
    desc: "GPSを許可するだけで、今いる場所の近くにいる女の子がマップ上に表示されます。",
    img: "https://readdy.ai/api/search-image?query=clean%20white%20smartphone%20mockup%20displaying%20bright%20map%20application%20interface%20with%20soft%20pink%20location%20pin%20icons%2C%20pure%20white%20background%2C%20minimal%20flat%20design%2C%20light%20airy%20aesthetic%2C%20pastel%20pink%20and%20white%20color%20palette%2C%20modern%20UI%20screenshot%2C%20crisp%20and%20fresh%20look%2C%20no%20dark%20elements%2C%20bright%20and%20open%20feel%2C%20high%20key%20lighting%2C%20very%20light%20and%20clean&width=320&height=220&seq=howto01v3white&orientation=landscape",
  },
  {
    step: "02",
    icon: "ri-user-heart-fill",
    title: "気になる子を見つける",
    desc: "マップのピンをタップして、プロフィール・写真・キャッチコピーをチェック！",
    img: "https://readdy.ai/api/search-image?query=beautiful%20young%20japanese%20woman%20smiling%20warmly%20at%20camera%2C%20soft%20pink%20bokeh%20background%2C%20elegant%20casual%20fashion%2C%20charming%20expression%2C%20portrait%20photography%2C%20clean%20white%20studio%20background&width=320&height=220&seq=howto02&orientation=landscape",
  },
  {
    step: "03",
    icon: "ri-heart-3-fill",
    title: "会いに行く♡",
    desc: "プロフィールの「会いに行く♡」ボタンからコンタクトを取って、素敵な出会いを楽しもう！",
    img: "https://readdy.ai/api/search-image?query=beautiful%20young%20japanese%20woman%20sitting%20inside%20cozy%20bright%20cafe%20shop%2C%20smiling%20happily%20and%20waiting%2C%20warm%20soft%20interior%20lighting%20with%20wooden%20furniture%20and%20plants%2C%20wearing%20cute%20casual%20fashion%2C%20cheerful%20and%20welcoming%20expression%2C%20lifestyle%20photography%2C%20warm%20cream%20and%20pink%20tones%2C%20inviting%20atmosphere%2C%20clearly%20japanese%20interior%20design&width=320&height=220&seq=howto03v2&orientation=landscape",
  },
];

// 30分単位のシードを返す（0〜47の整数、1日48スロット）
function get30MinSlot(): number {
  const now = new Date();
  return now.getHours() * 2 + Math.floor(now.getMinutes() / 30);
}

// シードベースの疑似乱数（0〜1）
function seededRandom(seed: number): number {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
}

// シードベースでarrayをシャッフル
function seededShuffle<T>(arr: T[], seed: number): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(seed + i) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

interface HomeDisplaySettings {
  count: number;
  orderBy: "sort_order" | "age_asc" | "age_desc" | "random";
  pinnedIds: string[];
}

const DEFAULT_HOME_SETTINGS: HomeDisplaySettings = {
  count: 8,
  orderBy: "sort_order",
  pinnedIds: [],
};

const HomePage = () => {
  const [characters, setCharacters] = useState<ReturnType<typeof rowToCharacter>[]>([]);
  const [currentImageIndexes, setCurrentImageIndexes] = useState<Record<string, number>>({});
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [homeSettings, setHomeSettings] = useState<HomeDisplaySettings>(DEFAULT_HOME_SETTINGS);
  const [showStickyBtn, setShowStickyBtn] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroFade, setHeroFade] = useState(true);
  const heroRef = useRef<HTMLDivElement>(null);

  const heroImages = useMemo(() => {
    return window.innerWidth < 768 ? HERO_IMAGES_MOBILE : HERO_IMAGES_DESKTOP;
  }, []);

  // Auto-cycle hero images every 5 seconds with fade transition
  useEffect(() => {
    if (heroImages.length <= 1) return;
    const timer = setInterval(() => {
      setHeroFade(false);
      setTimeout(() => {
        setHeroIndex((prev) => (prev + 1) % heroImages.length);
        setHeroFade(true);
      }, 600);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages]);

  useEffect(() => {
    const fetchAll = async () => {
      const [{ data: charsData }, { data: settingsData }] = await Promise.all([
        supabase.from("characters").select("*").order("sort_order", { ascending: true }),
        supabase.from("site_settings").select("value").eq("key", "home_display_settings").maybeSingle(),
      ]);

      if (charsData) {
        const chars = (charsData as CharacterRow[]).map(rowToCharacter);
        setCharacters(chars);
        const init: Record<string, number> = {};
        chars.forEach((c) => { init[c.id] = 0; });
        setCurrentImageIndexes(init);
      }

      if (settingsData?.value) {
        try {
          const parsed = JSON.parse(settingsData.value);
          setHomeSettings({ ...DEFAULT_HOME_SETTINGS, ...parsed });
        } catch {
          setHomeSettings(DEFAULT_HOME_SETTINGS);
        }
      }
    };

    fetchAll();
    setTimeout(() => setHeroLoaded(true), 100);
  }, []);

  const handleScroll = useCallback(() => {
    const heroEl = heroRef.current;
    if (!heroEl) return;
    const heroBottom = heroEl.getBoundingClientRect().bottom;
    setShowStickyBtn(heroBottom < 0);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const featuredGirls = useMemo(() => {
    const active = characters.filter((c) => c.isActive === true);
    const { count, orderBy, pinnedIds } = homeSettings;

    // Pinned chars first
    const pinned = pinnedIds
      .map((id) => active.find((c) => c.id === id))
      .filter(Boolean) as ReturnType<typeof rowToCharacter>[];
    const rest = active.filter((c) => !pinnedIds.includes(c.id));

    // Sort rest
    let sorted = [...rest];
    if (orderBy === "age_asc") sorted.sort((a, b) => (a.age ?? 999) - (b.age ?? 999));
    else if (orderBy === "age_desc") sorted.sort((a, b) => (b.age ?? 0) - (a.age ?? 0));
    else if (orderBy === "random") {
      const seed = get30MinSlot() * 137;
      sorted = seededShuffle(sorted, seed);
    }

    const combined = [...pinned, ...sorted];
    return combined.slice(0, count);
  }, [characters, homeSettings]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito', sans-serif" }}>
      <Navbar />

      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative overflow-hidden" style={{ minHeight: "100svh" }}>
        {/* ALL FREE ribbon */}
        <div
          className="absolute z-20 overflow-hidden"
          style={{ width: "180px", height: "180px", top: "64px", left: "0px", pointerEvents: "none" }}
        >
          <div
            className="absolute flex items-center justify-center bg-gradient-to-r from-emerald-400 to-green-500 text-white font-black tracking-widest"
            style={{
              width: "220px",
              top: "44px",
              left: "-48px",
              transform: "rotate(-45deg)",
              fontSize: "13px",
              letterSpacing: "0.12em",
              padding: "8px 0",
              boxShadow: "0 4px 16px rgba(16,185,129,0.5)",
              textShadow: "0 1px 4px rgba(0,0,0,0.2)",
            }}
          >
            ✦ ALL FREE ✦
          </div>
        </div>

        {/* Background image — slideshow */}
        <div className="absolute inset-0">
          {heroImages.map((src, i) => (
            <img
              key={src}
              src={src}
              alt="hero background"
              className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700"
              style={{ opacity: i === heroIndex ? (heroFade ? 1 : 0) : 0 }}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "auto"}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ))}
          <div className="absolute inset-0 bg-black/40 md:bg-transparent" />
          <div className="absolute top-0 left-0 right-0 h-2/3 bg-gradient-to-b from-black/55 via-black/30 to-transparent md:hidden" />
          <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-black/70 via-black/45 to-black/20" />
          <div className="absolute inset-0 hidden md:block bg-gradient-to-t from-black/60 via-transparent to-black/25" />
          <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-black/60 to-transparent md:hidden" />
          <div className="absolute inset-0 bg-gradient-to-br from-pink-900/15 via-transparent to-rose-900/10" />
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-1/4 right-10 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: "4s" }} />
        <div className="absolute bottom-1/3 left-10 w-48 h-48 bg-rose-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: "6s" }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col" style={{ minHeight: "100svh" }}>
          <div
            className={`flex-1 flex flex-col items-center justify-center text-center px-4 w-full max-w-4xl mx-auto pt-24 pb-4 md:pt-0 md:pb-0 transition-all duration-1000 ${heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs px-4 py-2 rounded-full mb-5 md:mb-8">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              今すぐ出会える女の子が近くにいます
            </div>

            {/* Logo */}
            <div className="flex justify-center mb-4 md:mb-6">
              <img
                src="https://public.readdy.ai/ai/img_res/db459360-20e7-4f18-828f-b6f77daa6c55.png"
                alt="Girls' Map"
                className="h-14 md:h-20 w-auto object-contain drop-shadow-2xl"
              />
            </div>

            {/* Main headline */}
            <h1
              className="text-4xl md:text-7xl font-bold text-white mb-3 md:mb-5 drop-shadow-2xl leading-tight"
              style={{ fontFamily: "'Pacifico', cursive", textShadow: "0 4px 32px rgba(236,72,153,0.8)" }}
            >
              Girls&apos; Map
            </h1>

            {/* Sub headline */}
            <p className="text-xl md:text-3xl font-bold mb-3 md:mb-4 drop-shadow-lg" style={{ color: "#ffd6e7", textShadow: "0 2px 12px rgba(0,0,0,0.5)" }}>
              あなたの近くに、Hな女の子がいるかも♡
            </p>
            <p className="text-xs md:text-base text-white/75 mb-5 md:mb-8 leading-relaxed max-w-xl mx-auto">
              GPSをONにするだけで、今すぐ近くにいる女の子をマップで発見。<br className="hidden md:block" />
              プロフィールを見て、気になったらすぐ会いに行こう。
            </p>

            {/* Trust indicators */}
            <div className="flex items-center justify-center gap-4 md:gap-6 mb-5 md:mb-6 text-white/70 text-xs">
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-green-400 text-sm"></i>
                安全・安心
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-lock-line text-green-400 text-sm"></i>
                プライバシー保護
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-24-hours-line text-green-400 text-sm"></i>
                24時間対応
              </span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col items-center gap-3 w-full max-w-xs md:max-w-sm mx-auto">
              <Link
                to="/map"
                className="relative inline-flex items-center justify-center gap-3 w-full px-6 py-5 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full font-black text-base md:text-lg hover:from-pink-600 hover:to-rose-600 transition-all whitespace-nowrap cursor-pointer overflow-hidden group"
                style={{ boxShadow: "0 10px 40px rgba(244,63,94,0.7)", letterSpacing: "0.02em" }}
              >
                <span className="absolute inset-0 rounded-full bg-white/20 animate-ping" style={{ animationDuration: "2s" }}></span>
                <span className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 rounded-full"></span>
                <i className="ri-crosshair-2-line text-xl md:text-2xl relative"></i>
                <span className="relative">今すぐ近くの女の子を探す</span>
                <i className="ri-arrow-right-line text-lg md:text-xl relative"></i>
              </Link>
              <Link
                to="/characters"
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2 bg-white/10 backdrop-blur-sm text-white/70 border border-white/25 rounded-full font-medium text-xs hover:bg-white/20 hover:text-white transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-heart-3-line text-xs"></i>
                女の子一覧を見る
              </Link>
            </div>
          </div>

          {/* Slide dots + scroll indicator */}
          <div className="relative z-10 flex flex-col items-center gap-2 pb-6">
            <div className="flex items-center gap-2">
              {heroImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setHeroFade(false); setTimeout(() => { setHeroIndex(i); setHeroFade(true); }, 300); }}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${i === heroIndex ? "w-5 h-2 bg-white" : "w-2 h-2 bg-white/40"}`}
                  aria-label={`画像${i + 1}`}
                />
              ))}
            </div>
            <span className="text-white/60 text-xs tracking-wide font-medium">Girls&apos; Map の詳細はコチラ</span>
            <i className="ri-arrow-down-s-line text-white/40 text-lg"></i>
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-50/80 to-rose-50/50" />
        <div className="relative max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {STATS.map((stat, i) => (
              <div
                key={i}
                className="relative bg-white rounded-2xl p-6 text-center overflow-hidden border border-pink-100 hover:border-pink-200 transition-all group"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
                <div className={`w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-2xl bg-gradient-to-br ${stat.color}`}>
                  <i className={`${stat.icon} text-white text-xl`}></i>
                </div>
                <p className="text-2xl md:text-3xl font-black text-gray-800 mb-1">{stat.value}</p>
                <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED GIRLS ===== */}
      <section className="py-20 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #fff 0%, #fff5f7 40%, #fff0f3 100%)" }}>
        <div className="absolute top-10 left-0 w-72 h-72 bg-pink-100/60 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-0 w-96 h-96 bg-rose-100/50 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-white border border-pink-200 text-pink-500 text-xs font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-4">
              <i className="ri-heart-3-fill text-rose-400"></i>
              Popular Girls
              <i className="ri-heart-3-fill text-rose-400"></i>
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-800 mt-2 mb-3">
              人気の女の子
            </h2>
            <p className="text-gray-400 text-sm">今日もマップ上で待ってるよ♡ タップしてプロフィールをチェック！</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {featuredGirls.map((girl, idx) => (
              <Link
                key={girl.id}
                to={`/profile/${girl.id}`}
                className="group relative bg-white rounded-3xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
                style={{ boxShadow: "0 4px 20px rgba(244,63,94,0.10)" }}
              >
                <div className="relative w-full overflow-hidden" style={{ height: "260px" }}>
                  <img
                    src={girl.images[currentImageIndexes[girl.id] ?? 0]}
                    alt={girl.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute top-3 left-3 w-7 h-7 flex items-center justify-center bg-gradient-to-br from-pink-500 to-rose-500 text-white text-xs font-black rounded-full">
                    {idx + 1}
                  </div>
                  <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                    <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                      <span>オンライン</span>
                    </div>
                    <div className="bg-white/90 text-pink-600 text-xs px-2 py-0.5 rounded-full font-black">
                      {girl.age != null ? `${girl.age}歳` : "ひみつ"}
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <h3 className="text-white font-black text-base leading-tight mb-1.5">{girl.name}</h3>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {girl.type && (
                        <span className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs px-2.5 py-0.5 rounded-full font-semibold">
                          {girl.type}
                        </span>
                      )}
                      {girl.tags.slice(0, 1).map((tag) => (
                        <span key={tag} className="bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="p-3 pb-3.5">
                  <div className="flex items-start gap-1.5 mb-3">
                    <i className="ri-double-quotes-l text-pink-300 text-base flex-shrink-0 mt-0.5"></i>
                    <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed font-medium italic">
                      {girl.catchphrase}
                    </p>
                  </div>
                  {girl.tags.length > 1 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {girl.tags.slice(1, 3).map((tag) => (
                        <span key={tag} className="text-xs bg-pink-50 text-pink-400 px-2 py-0.5 rounded-full border border-pink-100">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div
                    className="w-full py-2 rounded-full text-xs font-black text-white text-center flex items-center justify-center gap-1 transition-all group-hover:opacity-90"
                    style={{ background: "linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)" }}
                  >
                    <i className="ri-heart-fill text-xs"></i>
                    会いに行く♡
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/characters"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white border-2 border-pink-200 text-pink-500 rounded-full font-bold text-sm hover:bg-pink-50 transition-all cursor-pointer"
              style={{ boxShadow: "0 2px 12px rgba(244,63,94,0.10)" }}
            >
              <i className="ri-heart-3-line"></i>
              すべての女の子を見る
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-24 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #fff5f7 0%, #fff0f3 50%, #fdf2f8 100%)" }}>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pink-200 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pink-200 to-transparent" />

        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-white text-pink-500 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-3 border border-pink-100">
              ✦ How it works ✦
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-800 mt-2 mb-3">
              かんたん3ステップ
            </h2>
            <p className="text-gray-400 text-sm">登録不要・無料で今すぐ使えます</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-16 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-pink-200 via-rose-300 to-pink-200" />
            {HOW_IT_WORKS.map((item, i) => (
              <div key={i} className="relative bg-white rounded-3xl overflow-hidden border border-pink-100 hover:border-pink-200 transition-all group">
                <div className="relative w-full h-44 overflow-hidden">
                  <img
                    src={item.img}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute top-4 left-4 w-10 h-10 flex items-center justify-center bg-gradient-to-br from-pink-500 to-rose-500 text-white font-black text-sm rounded-full">
                    {item.step}
                  </div>
                </div>
                <div className="p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-pink-50 rounded-xl mb-3">
                    <i className={`${item.icon} text-pink-500 text-lg`}></i>
                  </div>
                  <h3 className="font-black text-gray-800 text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== APPEAL SECTION ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Left: Image collage */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl overflow-hidden" style={{ height: "220px" }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=beautiful%20young%20japanese%20woman%20with%20long%20black%20hair%2C%20soft%20smile%2C%20wearing%20elegant%20white%20dress%2C%20clean%20white%20background%2C%20portrait%20photography%2C%20feminine%20and%20charming%2C%20warm%20lighting&width=200&height=280&seq=appeal01&orientation=portrait"
                    alt="girl 1"
                    loading="lazy"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden mt-8" style={{ height: "220px" }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=cute%20young%20japanese%20woman%20with%20short%20hair%2C%20playful%20smile%2C%20casual%20chic%20fashion%2C%20pink%20pastel%20background%2C%20portrait%20photography%2C%20bright%20and%20cheerful%20expression&width=200&height=280&seq=appeal02&orientation=portrait"
                    alt="girl 2"
                    loading="lazy"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden -mt-4" style={{ height: "200px" }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=elegant%20young%20japanese%20woman%20with%20wavy%20hair%2C%20gentle%20smile%2C%20stylish%20outfit%2C%20soft%20cream%20background%2C%20portrait%20photography%2C%20sophisticated%20and%20beautiful&width=200&height=280&seq=appeal03&orientation=portrait"
                    alt="girl 3"
                    loading="lazy"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden mt-4" style={{ height: "200px" }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=pretty%20young%20japanese%20woman%20with%20ponytail%2C%20bright%20smile%2C%20trendy%20fashion%2C%20light%20pink%20background%2C%20portrait%20photography%2C%20fresh%20and%20lively&width=200&height=280&seq=appeal04&orientation=portrait"
                    alt="girl 4"
                    loading="lazy"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-br from-pink-500 to-rose-500 text-white rounded-2xl p-4 text-center">
                <p className="text-2xl font-black">120+</p>
                <p className="text-xs text-pink-100">登録中の女の子</p>
              </div>
            </div>

            {/* Right: Text */}
            <div>
              <span className="inline-block bg-pink-50 text-pink-500 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
                ✦ About Girls&apos; Map ✦
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-gray-800 mb-5 leading-tight">
                今すぐ会える女の子が<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">
                  あなたの近くにいる
                </span>
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Girls&apos; Mapは、GPSを使ってあなたの現在地から近くにいる女の子をリアルタイムで表示するサービスです。
                登録不要・無料で今すぐ使えます。気になる子を見つけたら、プロフィールをチェックして会いに行こう！
              </p>
              <div className="flex flex-col gap-3 mb-8">
                {[
                  { icon: "ri-map-pin-2-fill", text: "現在地から近い順に表示" },
                  { icon: "ri-refresh-fill", text: "15分ごとに位置情報を更新" },
                  { icon: "ri-user-heart-fill", text: "120人以上の女の子が登録中" },
                  { icon: "ri-shield-check-fill", text: "安全・安心・プライバシー保護" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-pink-50 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-pink-500 text-sm`}></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/map"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full font-black text-sm hover:from-pink-600 hover:to-rose-600 transition-all cursor-pointer whitespace-nowrap"
                style={{ boxShadow: "0 6px 24px rgba(244,63,94,0.4)" }}
              >
                <i className="ri-crosshair-2-line"></i>
                マップで探してみる
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=romantic%20soft%20pink%20rose%20petals%20falling%2C%20dreamy%20bokeh%20background%2C%20warm%20pink%20and%20rose%20gold%20tones%2C%20elegant%20feminine%20atmosphere%2C%20abstract%20beauty%20background%2C%20soft%20focus&width=1200&height=500&seq=ctabg2026v2&orientation=landscape"
            alt="cta background"
            loading="lazy"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-pink-600/85 via-rose-500/80 to-pink-600/85" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 text-white/90 text-xs px-4 py-2 rounded-full mb-6">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            今この瞬間も女の子たちが待っています
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold text-white mb-5 drop-shadow-lg"
            style={{ fontFamily: "'Pacifico', cursive", textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}
          >
            さあ、探しに行こう♡
          </h2>
          <p className="text-pink-100 mb-10 text-sm md:text-base leading-relaxed">
            登録不要・完全無料。GPSをONにするだけで、<br className="hidden md:block" />
            今すぐあなたの近くにいる女の子が見つかります。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/map"
              className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-white text-pink-600 rounded-full font-black text-base hover:bg-pink-50 transition-all cursor-pointer whitespace-nowrap"
              style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}
            >
              <i className="ri-crosshair-2-line text-lg"></i>
              今すぐマップで探す
            </Link>
            <Link
              to="/characters"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 backdrop-blur-sm text-white border-2 border-white/40 rounded-full font-bold text-sm hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-heart-3-fill"></i>
              女の子一覧を見る
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* ===== STICKY BOTTOM CTA ===== */}
      <div
        className={`md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-5 pt-3 transition-all duration-300 ${
          showStickyBtn ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        style={{ background: "linear-gradient(to top, rgba(255,255,255,1) 65%, rgba(255,255,255,0))" }}
      >
        <Link
          to="/map"
          className="flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600 text-white rounded-2xl font-black text-base cursor-pointer active:scale-95 transition-transform whitespace-nowrap"
          style={{ boxShadow: "0 6px 28px rgba(244,63,94,0.6)" }}
        >
          <i className="ri-crosshair-2-line text-lg"></i>
          <span>今すぐ近くの女の子を探す</span>
          <i className="ri-arrow-right-line text-base"></i>
        </Link>
      </div>
    </div>
  );
};

export default HomePage;
